import type { Performance, Rink, Markings } from "../data/schema";
import { RinkGeometry } from "../geometry/rink";
import { Scene } from "../render/scene";
import { ChoreoEngine } from "../engine/ChoreoEngine";

const NS = "http://www.w3.org/2000/svg";
const fmt = (t: number) => `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, "0")}`;

function h<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text) e.textContent = text;
  return e;
}

export interface PlayerOpts { perf: Performance; rink: Rink; markings: Markings; musicUrl: string; }

export class Player {
  private engine: ChoreoEngine;
  private scene: Scene;
  private audio = new Audio();
  private raf = 0;
  private els: Record<string, HTMLElement> = {};
  private chips: HTMLElement[] = [];
  private stepItems: HTMLElement[] = [];
  private strip!: HTMLElement;
  private tickerWrap!: HTMLElement;
  private save: () => void = () => {};
  private lastSave = 0;

  constructor(root: HTMLElement, opts: PlayerOpts) {
    const { perf, rink, markings, musicUrl } = opts;
    this.engine = new ChoreoEngine(perf);

    const stage = h("div", "stage");
    const header = h("div", "topbar");
    const name = h("span", "name", `${perf.meta.team ?? ""} — ${perf.meta.theme ?? perf.meta.id}`);
    const sect = h("span", "badge", "");
    const spacer = h("span", "spacer");
    const time = h("span", "badge", "0:00");
    header.append(name, sect, spacer, time);

    const svg = document.createElementNS(NS, "svg");
    const geo = new RinkGeometry(rink, 1280);
    this.scene = new Scene(svg, geo, perf, markings);

    // ticker
    this.tickerWrap = h("div", "ticker");
    this.strip = h("div", "ticker-strip");
    perf.steps.forEach((s) => { const it = h("span", "tstep", s.text); this.stepItems.push(it); this.strip.append(it); });
    this.tickerWrap.append(this.strip);

    // single controls line: transport + speed + volume
    const controls = h("div", "controls");
    const prev = h("button", "", "◀ Prev");
    const back5 = h("button", "", "-5");
    const play = h("button", "primary", "▶ Play");
    const fwd5 = h("button", "", "+5");
    const next = h("button", "", "Next ▶");
    const spLabel = h("label", "sel", "speed ");
    const spSel = h("select");
    for (const v of ["0.5", "0.75", "1", "1.5", "2"]) { const o = h("option"); o.value = v; o.textContent = v + "×"; if (v === "1") o.selected = true; spSel.append(o); }
    spLabel.append(spSel);
    const volWrap = h("label", "sel", "");
    const mute = h("button", "icon", "🔊");
    const vol = h("input") as HTMLInputElement;
    vol.type = "range"; vol.min = "0"; vol.max = "100"; vol.value = "80"; vol.className = "vol";
    volWrap.append(mute, vol);
    controls.append(prev, back5, play, fwd5, next, spLabel, volWrap);

    // top-right (header): Layout + Show Names
    const mkLabel = h("label", "sel", "Layout ");
    const mkSel = h("select");
    for (const n of this.scene.markingNames()) { const o = h("option"); o.value = n; o.textContent = markings[n].label ?? n; if (n === perf.meta.defaultMarkings) o.selected = true; mkSel.append(o); }
    mkLabel.append(mkSel);
    const namesWrap = h("label", "sel chk");
    const namesCb = h("input") as HTMLInputElement; namesCb.type = "checkbox"; namesCb.checked = true;
    namesWrap.append(namesCb, document.createTextNode(" Show Names"));
    header.append(mkLabel, namesWrap);

    const seek = h("input", "seek") as HTMLInputElement;
    seek.type = "range"; seek.min = "0"; seek.max = String(perf.music.durationSec); seek.step = "0.05"; seek.value = "0";

    const chips = h("div", "chips");
    perf.sections.forEach((s) => { const c = h("button", "chip" + (s.status === "draft" ? " draft" : ""), `${fmt(s.atSec)}  ${s.label}`); c.onclick = () => this.jumpTo(s.atSec); chips.append(c); this.chips.push(c); });

    const legend = h("div", "legend");
    for (const s of perf.skaters) { const sp = h("span"); sp.innerHTML = `<i style="background:${s.color}"></i>${s.name}`; legend.append(sp); }

    stage.append(header, svg, this.tickerWrap, controls, seek, chips, legend);
    root.append(stage);
    this.els = { time, sect, seek };

    // ---- persisted prefs (localStorage, per performance) ----
    const PREF = "choreo_" + perf.meta.id;
    let prefs: { marks?: string; names?: boolean; t?: number; rate?: number; vol?: number; muted?: boolean } = {};
    try { prefs = JSON.parse(localStorage.getItem(PREF) || "{}"); } catch { /* ignore */ }
    this.save = () => {
      try { localStorage.setItem(PREF, JSON.stringify({ marks: mkSel.value, names: namesCb.checked, t: this.audio.currentTime, rate: this.audio.playbackRate, vol: this.audio.volume, muted: this.audio.muted })); } catch { /* ignore */ }
    };

    // behaviour
    this.audio.src = musicUrl;
    this.audio.preload = "auto";
    this.audio.volume = 0.8;
    const skip = (d: number) => { this.audio.currentTime = Math.max(0, Math.min(perf.music.durationSec, this.audio.currentTime + d)); this.render(); this.save(); };
    play.onclick = () => (this.audio.paused ? this.audio.play() : this.audio.pause());
    back5.onclick = () => skip(-5);
    fwd5.onclick = () => skip(5);
    prev.onclick = () => this.step(-1);
    next.onclick = () => this.step(1);
    this.audio.addEventListener("play", () => { play.textContent = "⏸ Pause"; this.loop(); });
    this.audio.addEventListener("pause", () => { play.textContent = "▶ Play"; cancelAnimationFrame(this.raf); this.save(); });
    seek.oninput = () => { this.audio.currentTime = parseFloat(seek.value); this.render(); this.save(); };
    spSel.onchange = () => { this.audio.playbackRate = parseFloat(spSel.value); this.save(); };
    mkSel.onchange = () => { this.scene.setMarkings(mkSel.value); this.save(); };
    vol.oninput = () => { this.audio.volume = parseInt(vol.value) / 100; this.audio.muted = false; mute.textContent = this.audio.volume ? "🔊" : "🔇"; this.save(); };
    mute.onclick = () => { this.audio.muted = !this.audio.muted; mute.textContent = this.audio.muted ? "🔇" : "🔊"; this.save(); };
    namesCb.onchange = () => { this.scene.setNamesVisible(namesCb.checked); this.save(); };
    document.addEventListener("keydown", (e) => {
      if (e.key === " ") { e.preventDefault(); play.click(); }
      else if (e.key === "ArrowRight") skip(5);
      else if (e.key === "ArrowLeft") skip(-5);
    });

    // apply restored prefs
    if (prefs.marks && this.scene.markingNames().includes(prefs.marks)) { mkSel.value = prefs.marks; this.scene.setMarkings(prefs.marks); }
    if (prefs.names === false) { namesCb.checked = false; this.scene.setNamesVisible(false); }
    if (typeof prefs.vol === "number") { this.audio.volume = prefs.vol; vol.value = String(Math.round(prefs.vol * 100)); }
    if (prefs.muted) { this.audio.muted = true; mute.textContent = "🔇"; }
    if (prefs.rate) { this.audio.playbackRate = prefs.rate; spSel.value = String(prefs.rate); }
    const restoreT = () => { if (prefs.t && prefs.t > 0) { try { this.audio.currentTime = prefs.t; } catch { /* ignore */ } } this.render(); };
    if (this.audio.readyState > 0) restoreT(); else this.audio.addEventListener("loadedmetadata", restoreT, { once: true });

    this.render();
  }

  private loop = () => { this.render(); if (!this.audio.paused) this.raf = requestAnimationFrame(this.loop); };
  private jumpTo(sec: number) { this.audio.currentTime = sec + 0.001; this.render(); this.save(); }
  private step(dir: number) {
    const t = this.audio.currentTime;
    const secs = this.engine.perf.sections.map((s) => s.atSec);
    let idx = 0;
    for (let i = 0; i < secs.length; i++) if (secs[i] <= t + 0.25) idx = i;
    this.jumpTo(secs[Math.max(0, Math.min(secs.length - 1, idx + dir))]);
  }

  private render() {
    const t = this.audio.currentTime + (this.engine.perf.music.offsetSec ?? 0);
    this.scene.update(this.engine.sampleAt(t), t);
    this.els.time.textContent = "♪ " + fmt(this.audio.currentTime);
    (this.els.seek as HTMLInputElement).value = String(this.audio.currentTime);
    if (Math.abs(this.audio.currentTime - this.lastSave) > 2) { this.lastSave = this.audio.currentTime; this.save(); }

    const secs = this.engine.perf.sections;
    let si = 0;
    for (let i = 0; i < secs.length; i++) if (secs[i].atSec <= t) si = i;
    this.els.sect.textContent = secs[si]?.label ?? "";
    this.chips.forEach((c, i) => c.classList.toggle("active", i === si));

    const steps = this.engine.perf.steps;
    let active = -1;
    for (let i = 0; i < steps.length; i++) if (steps[i].atSec <= t) active = i;
    this.stepItems.forEach((it, i) => { it.classList.toggle("active", i === active); it.classList.toggle("past", i < active); it.classList.toggle("future", i > active); });
    if (this.stepItems.length) {
      const idx = Math.max(0, active);
      const a = this.stepItems[idx];
      let centerX = a.offsetLeft + a.offsetWidth / 2;
      const nx = idx + 1;
      if (active >= 0 && nx < steps.length) {
        const span = steps[nx].atSec - steps[idx].atSec;
        const frac = span > 0 ? Math.min(1, Math.max(0, (t - steps[idx].atSec) / span)) : 0;
        const b = this.stepItems[nx];
        centerX += (b.offsetLeft + b.offsetWidth / 2 - centerX) * frac;
      }
      this.strip.style.transform = `translateX(${this.tickerWrap.clientWidth / 2 - centerX}px)`;
    }
  }
}
